import { useResolveConflict, useSyncConflicts } from '@/api/hooks';
import { getErrorMessage } from '@/lib/api';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { ListPagination } from '@/components/ui/ListPagination';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateTime } from '@/lib/format';
import { canManage } from '@/lib/rbac';
import { Shuffle } from 'lucide-react';
import { useEffect, useState } from 'react';

export function SyncConflictsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('pending');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useSyncConflicts(tab, page);
  const resolve = useResolveConflict();
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    setPage(1);
  }, [tab]);

  if (!user) return null;
  if (!canManage(user.role)) {
    return (
      <div>
        <PageHeader title="Conflitos de sincronização" />
        <Alert variant="warning">
          Como operador, você pode ver que existem conflitos pendentes no painel — mas apenas
          proprietário ou gerente podem resolvê-los.
        </Alert>
        <Card className="mt-6">
          <CardBody className="py-12 text-center text-manejai-bark/70">
            {data?.meta.pending_count ?? 0} conflito(s) aguardando decisão.
          </CardBody>
        </Card>
      </div>
    );
  }

  async function handleResolve(id: number, resolution: string) {
    setError('');
    try {
      await resolve.mutateAsync({
        id,
        resolution,
        resolution_notes: notes[id] || undefined,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao resolver.'));
    }
  }

  return (
    <div>
      <PageHeader
        title="Conflitos de sincronização"
        subtitle="Diferenças entre celular e servidor — resolva manualmente"
      />

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      <div className="flex gap-2 mb-6">
        {['pending', 'all'].map((t) => (
          <Button
            key={t}
            size="sm"
            variant={tab === t ? 'primary' : 'outline'}
            onClick={() => setTab(t)}
          >
            {t === 'pending' ? `Pendentes (${data?.meta.pending_count ?? 0})` : 'Todos'}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="h-48 rounded-2xl bg-manejai-sand/40 animate-pulse" />
      ) : (data?.conflicts.length ?? 0) === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <Shuffle className="h-10 w-10 mx-auto text-manejai-sage mb-3" />
            <p className="text-manejai-bark/70">Nenhum conflito {tab === 'pending' ? 'pendente' : 'registrado'}.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          {data?.conflicts.map((c) => (
            <Card key={c.id}>
              <CardHeader className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-manejai-forest capitalize">
                    {c.entity_type} · v{c.client_base_version} → v{c.server_sync_version}
                  </p>
                  <p className="text-xs text-manejai-bark/60">{formatDateTime(c.created_at)}</p>
                </div>
                <Badge tone={c.resolution === 'pending' ? 'warning' : 'success'}>{c.resolution}</Badge>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4 text-xs">
                  <div className="rounded-xl bg-manejai-cream/50 p-3">
                    <p className="font-medium text-manejai-bark mb-1">Servidor</p>
                    <pre className="overflow-auto text-manejai-bark/70 whitespace-pre-wrap">
                      {JSON.stringify(c.server_payload, null, 2)}
                    </pre>
                  </div>
                  <div className="rounded-xl bg-manejai-sand/30 p-3">
                    <p className="font-medium text-manejai-bark mb-1">Celular</p>
                    <pre className="overflow-auto text-manejai-bark/70 whitespace-pre-wrap">
                      {JSON.stringify(c.client_payload, null, 2)}
                    </pre>
                  </div>
                </div>

                {c.resolution === 'pending' && (
                  <>
                    <Input
                      label="Notas da resolução"
                      value={notes[c.id] ?? ''}
                      onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })}
                      placeholder="Opcional — descreva a decisão tomada"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleResolve(c.id, 'server_wins')} loading={resolve.isPending}>
                        Manter servidor
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => handleResolve(c.id, 'client_wins')} loading={resolve.isPending}>
                        Aceitar celular
                      </Button>
                    </div>
                  </>
                )}

                {c.resolution_notes && (
                  <p className="text-sm text-manejai-bark/70">
                    <span className="font-medium">Notas:</span> {c.resolution_notes}
                  </p>
                )}
              </CardBody>
            </Card>
          ))}
          <div className="overflow-hidden rounded-2xl border border-manejai-sand/80 bg-white">
            <ListPagination
              page={page}
              lastPage={data?.meta.last_page ?? 1}
              summary={`${data?.meta.total ?? 0} conflito(s)`}
              onPageChange={setPage}
              className="border-t-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}
